import type { BugReportPriority, BugReportStatus } from '../../lib/bugReportsApi';
import type { TaskPriority } from '../../lib/tasksApi';
import type { TaskStatusCategory } from '../../lib/taskStatusesApi';
import { useI18n } from '../../contexts/I18nContext';

// Shared labels, colours and icon mappings for bug reports, so the list, the
// detail sidebar and the activity log never drift apart on what "IN_PROGRESS"
// looks like.
//
// Statuses and priorities borrow the task icons rather than inventing a second
// visual language: a bug report is triaged the way a ticket is, and the detail
// page sits right next to the task page in everyone's head.

/**
 * Which task status shape each bug status draws as: an empty ring for one
 * nobody has picked up, a half-filled one in progress, a tick when it is
 * fixed, a cross when it is turned down.
 */
export const BUG_STATUS_CATEGORY: Record<BugReportStatus, TaskStatusCategory> = {
  NEW: 'UNSTARTED',
  IN_PROGRESS: 'STARTED',
  RESOLVED: 'COMPLETED',
  REJECTED: 'CANCELLED',
};

export const BUG_STATUS_COLOR: Record<BugReportStatus, string> = {
  NEW: '#3b82f6',
  IN_PROGRESS: '#eab308',
  RESOLVED: '#22c55e',
  REJECTED: '#6b7280',
};

/** Bug priorities map onto the task priority icons; CRITICAL is task URGENT. */
export const BUG_PRIORITY_AS_TASK: Record<BugReportPriority, TaskPriority> = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'URGENT',
};

export const BUG_PRIORITY_COLOR: Record<BugReportPriority, string> = {
  LOW: '#9ca3af',
  MEDIUM: '#eab308',
  HIGH: '#f97316',
  CRITICAL: '#ef4444',
};

export function useBugLabels() {
  const { t } = useI18n();
  return {
    status: (value: BugReportStatus) => t(`bugReports.status.${value}`),
    priority: (value: BugReportPriority) => t(`bugReports.priority.${value}`),
  };
}

const STATUS_PILL: Record<BugReportStatus, string> = {
  NEW: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-700',
  RESOLVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-gray-200 text-gray-600',
};

const PRIORITY_PILL: Record<BugReportPriority, string> = {
  LOW: 'bg-gray-100 text-gray-600',
  MEDIUM: 'bg-sky-100 text-sky-700',
  HIGH: 'bg-orange-100 text-orange-700',
  CRITICAL: 'bg-red-100 text-red-700',
};

const PILL = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';

export function StatusBadge({ status }: { status: BugReportStatus }) {
  const labels = useBugLabels();
  return <span className={`${PILL} ${STATUS_PILL[status]}`}>{labels.status(status)}</span>;
}

export function PriorityBadge({ priority }: { priority: BugReportPriority }) {
  const labels = useBugLabels();
  return <span className={`${PILL} ${PRIORITY_PILL[priority]}`}>{labels.priority(priority)}</span>;
}

export function formatDateTime(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleString() : '—';
}
